import { Inject, Injectable } from '@angular/core';
import Dexie, { liveQuery } from 'dexie';
import { FileEntry } from './file-entry';
import { EncryptionService, PREFIX_ENCRYPTED_CONTENT_LEGACY, UpgradableEncryption } from '@evorait/pace-encryption';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { EncryptionMigrator } from '../../../../../pace-encryption/src/lib/services/encryption-migrator.service';
import { filter, firstValueFrom, from, Observable } from 'rxjs';
import { isDefined } from '../../utilities/is-defined';

@Injectable()
export class FileDexieService extends Dexie implements UpgradableEncryption {
    public static readonly DB_TABLE = 'fileEntries';
    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = FileDexieService.VERSION_ONE + 1;

    // The name fileEntries is misleading, and calling it fileTables would be a better name: But then we also need to
    // rename the table in the Dexie scheme definition, and would lose downward compatibility
    private fileEntries: Dexie.Table<FileEntry, number>;

    public constructor(
        private readonly encryption: EncryptionService,
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        private readonly encryptionMigrator: EncryptionMigrator,
    ) {
        super('FileDatabase');
        this.version(FileDexieService.VERSION_ONE).stores({
            fileEntries: 'fileName',
        });

        this.version(FileDexieService.VERSION_TWO).upgrade(async (trans) => {
            await firstValueFrom(this.encryption.selectInitialized.pipe(filter(Boolean)));
            const entriesToUpgrade = await trans
                .table(FileDexieService.DB_TABLE)
                .toCollection()
                .filter((entry) => !!entry.content)
                .toArray();

            if (entriesToUpgrade.length > 0) {
                const encryptionResults = await Promise.allSettled(
                    entriesToUpgrade.map(async (entry: FileEntry) => {
                        if (!entry.content) {
                            return;
                        }
                        const base64Content = await EncodingUtilities.base64EncodeBlob(entry.content);
                        await this.fileEntries.update(entry, {
                            encryptedContent: await Dexie.waitFor(this.encryption.encryptAsync(base64Content)),
                            mimeType: entry.content?.type,
                            content: undefined,
                        });
                    }),
                );

                const failed = encryptionResults.filter((r) => r.status === 'rejected');
                if (failed.length) {
                    console.error(this.constructor.name, `${failed.length} entries failed to encrypt...`);
                }
            }
        });
        this.fileEntries.mapToClass(FileEntry);
    }

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return from(
            this.fileEntries
                .toCollection()
                .filter((entry) => entry.encryptedContent.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY))
                .count()
                .then((count) => count > 0),
        );
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        return from(
            this.transaction('rw', this.fileEntries, async () => {
                const fileEntriesToMigrate = await this.fileEntries
                    .toCollection()
                    .filter((entry) => entry.encryptedContent.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY))
                    .toArray();

                const results = await Promise.allSettled(
                    fileEntriesToMigrate.map(async (entry: FileEntry) => {
                        const encrypted = await Dexie.waitFor(
                            this.encryptionMigrator.reEncrypt(entry.encryptedContent),
                        );
                        return this.fileEntries.update(entry, { encryptedContent: encrypted });
                    }),
                );

                const failed = results.filter((r) => r.status === 'rejected');
                if (failed.length) {
                    console.error(this.constructor.name, `${failed.length} entries failed to migrate`);
                    throw new Error(`${failed.length} entries failed to migrate`);
                }
            }).then(() => true),
        );
    }

    /**
     * Get file from database by name
     *
     * @param fileName Name of file i.e. Title
     * @returns Promise with file in Blob format
     */
    public async getFile(fileName: string): Promise<{ file: Blob; timestamp: number }> {
        return this.fileEntries.get({ fileName }).then(
            (file) =>
                new Promise((resolve, reject) => {
                    if (file?.content) {
                        // this is legacy unencrypted content. so we can return it directly, but should still encrypt it in indexeddb...
                        this.writeFileEntry(file.fileName, file.content).catch((error) => this.log.error(error));
                        return resolve({ file: file.content, timestamp: file.timestamp });
                    }
                    if (file) {
                        this.encryption
                            .decryptAsync(file.encryptedContent)
                            .then((decryptedBase64) =>
                                resolve({
                                    file: EncodingUtilities.base64DecodeBlob(decryptedBase64, file.mimeType),
                                    timestamp: file.timestamp,
                                }),
                            )
                            .catch((error) => {
                                throw Error(`FileDexieService: getFileByName: Failed to decrypt content: ${error}`);
                            });
                    } else {
                        // returning code: 1 in object format in case of file not exist to meet the existing code
                        // conditions/requirements which was written to handle the same case in ionic file storage.
                        // Ionic file storage throws error in this format in case of file not exists.
                        reject({ code: 1 });
                    }
                }),
        );
    }

    // TODO: What about the previously thrown errors?
    //       --> replace with logic that creates expected data not on error, but on event with payload 'undefined'
    public selectFile(fileName: string): Observable<{ file: Blob; timestamp: number } | undefined> {
        return from(
            liveQuery(() =>
                this.fileEntries
                    .where('fileName')
                    .equals(fileName)
                    // while the database query allows for multiple returned values, we know from the business logic
                    // that there will be at most one match for a given filename; therefore we return that specific value
                    // if it exists
                    .first()
                    .then((file) =>
                        isDefined(file)
                            ? this.encryption.decryptAsync(file.encryptedContent).then((decryptedBase64) => ({
                                  file: EncodingUtilities.base64DecodeBlob(decryptedBase64, file.mimeType),
                                  timestamp: file.timestamp,
                              }))
                            : undefined,
                    )
                    .catch((error: Error) => {
                        this.log.error(`FileDexieService: selectFile: unknown error occurred: ${error}`);
                        return undefined;
                    }),
            ),
        );
    }

    /**
     * Write file entry to the database
     *
     * @param fileName Name of file i.e. Title
     * @param data Content of file in Blob format
     * @param timestamp time of triggering the file writing.
     * @returns Promise with the result of the write operation
     */
    public async writeFileEntry(fileName: string, data: Blob, timestamp: number = Date.now()): Promise<number> {
        // Used toDate().getTime() here on purpose to allow unit tests to manipulate the clock.
        // Used put here as it would add record if it doesn't exist by checking on fileName otherwise just updates it.
        return EncodingUtilities.base64EncodeBlob(data)
            .then((base64Content) => this.encryption.encryptAsync(base64Content))
            .then((encryptedContent) =>
                this.fileEntries.put({
                    timestamp,
                    fileName: fileName,
                    encryptedContent,
                    mimeType: data.type,
                }),
            )
            .catch((error) => {
                throw Error(`FileDexieService: writeFileEntry: Error on writing file entry to database: ${error}`);
            });
    }

    /**
     * Remove file entry from the database.
     *
     * @param fileName Name of file i.e. Title
     * @returns Promise with the result of the delete operation
     */
    public removeFileEntry(fileName: string): Promise<number> {
        return this.fileEntries
            .where({ fileName: fileName })
            .delete()
            .catch((error) => {
                throw Error(`FileDexieService: removeFileEntry: Error on deleting the file from database: ${error}`);
            });
    }
}
